<script>
	import { onMount, onDestroy, createEventDispatcher } from "svelte";
	import {
		ClassicEditor,
		Alignment,
		Autoformat,
		AutoImage,
		AutoLink,
		Autosave,
		BalloonToolbar,
		BlockQuote,
		Bold,
		Bookmark,
		Code,
		CodeBlock,
		Emoji,
		Essentials,
		FindAndReplace,
		FontBackgroundColor,
		FontColor,
		FontFamily,
		FontSize,
		FullPage,
		Fullscreen,
		GeneralHtmlSupport,
		Heading,
		Highlight,
		HorizontalLine,
		HtmlComment,
		HtmlEmbed,
		ImageBlock,
		ImageCaption,
		ImageInline,
		ImageInsert,
		ImageInsertViaUrl,
		ImageResize,
		ImageStyle,
		ImageTextAlternative,
		ImageToolbar,
		ImageUpload,
		Indent,
		IndentBlock,
		Italic,
		Link,
		LinkImage,
		List,
		ListProperties,
		MediaEmbed,
		Mention,
		PageBreak,
		Paragraph,
		PasteFromMarkdownExperimental,
		PasteFromOffice,
		PlainTableOutput,
		RemoveFormat,
		ShowBlocks,
		SimpleUploadAdapter,
		SourceEditing,
		SpecialCharacters,
		SpecialCharactersArrows,
		SpecialCharactersCurrency,
		SpecialCharactersEssentials,
		SpecialCharactersLatin,
		SpecialCharactersMathematical,
		SpecialCharactersText,
		Strikethrough,
		Style,
		Subscript,
		Superscript,
		Table,
		TableCaption,
		TableCellProperties,
		TableColumnResize,
		TableLayout,
		TableProperties,
		TableToolbar,
		TextPartLanguage,
		TextTransformation,
		Title,
		TodoList,
		Underline,
		WordCount,
	} from "ckeditor5";

	import translations from "ckeditor5/translations/ko.js";
	import "ckeditor5/ckeditor5.css";

	const dispatch = createEventDispatcher();
	const LICENSE_KEY = "GPL";

	// Props
	export let value = "";
	export let config = {};
	export let disabled = false;
	export let pendingFiles = new Map();

	// 내부 상태
	let element;
	let editor;
	let ready = false;
	let readOnlyLockId = "svelte-component-lock"; // 읽기 전용 모드 ID
	let currentDisabledState = false; // 현재 비활성화 상태 추적

	// 커스텀 플러그인
	// 파일 지연 업로드 플러그인
	class DelayedUploadAdapter {
		constructor(loader) {
			this.loader = loader;
			this.tempId = `${Date.now()}_${Math.random().toString(16).substr(2, 8)}`;
		}

		upload() {
			return this.loader.file.then((file) => {
				// 1. 임시 URL 생성 (실제 업로드 X)
				const tempUrl = URL.createObjectURL(file);

				// 2. 파일을 대기열에 추가
				pendingFiles.set(this.tempId, {
					file,
					tempUrl,
					resolved: false,
				});

				return { default: tempUrl };
			});
		}

		abort() {
			pendingFiles.delete(this.tempId);
		}
	}

	function DelayedUploadAdapterPlugin(editor) {
		editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
			// 여기서 커스텀 어댑터 인스턴스를 반환
			return new DelayedUploadAdapter(loader);
		};
	}

	// 에디터 초기화
	onMount(async () => {
		try {
			// 기본 설정과 사용자 설정 병합
			const defaultConfig = {
				toolbar: {
					items: [
						"undo",
						"redo",
						"|",
						"sourceEditing",
						"showBlocks",
						"findAndReplace",
						"textPartLanguage",
						"fullscreen",
						"|",
						"heading",
						"style",
						"|",
						"fontSize",
						"fontFamily",
						"fontColor",
						"fontBackgroundColor",
						"|",
						"bold",
						"italic",
						"underline",
						"strikethrough",
						"subscript",
						"superscript",
						"code",
						"removeFormat",
						"|",
						"emoji",
						"specialCharacters",
						"horizontalLine",
						"pageBreak",
						"link",
						"bookmark",
						"insertImage",
						"mediaEmbed",
						"insertTable",
						"insertTableLayout",
						"highlight",
						"blockQuote",
						"codeBlock",
						"htmlEmbed",
						"|",
						"alignment",
						"|",
						"bulletedList",
						"numberedList",
						"todoList",
						"outdent",
						"indent",
					],
					shouldNotGroupWhenFull: true,
				},
				plugins: [
					Alignment,
					Autoformat,
					AutoImage,
					AutoLink,
					Autosave,
					BalloonToolbar,
					BlockQuote,
					Bold,
					Bookmark,
					Code,
					CodeBlock,
					Emoji,
					Essentials,
					FindAndReplace,
					FontBackgroundColor,
					FontColor,
					FontFamily,
					FontSize,
					FullPage,
					Fullscreen,
					GeneralHtmlSupport,
					Heading,
					Highlight,
					HorizontalLine,
					HtmlComment,
					HtmlEmbed,
					ImageBlock,
					ImageCaption,
					ImageInline,
					ImageInsert,
					ImageInsertViaUrl,
					ImageResize,
					ImageStyle,
					ImageTextAlternative,
					ImageToolbar,
					ImageUpload,
					Indent,
					IndentBlock,
					Italic,
					Link,
					LinkImage,
					List,
					ListProperties,
					MediaEmbed,
					Mention,
					PageBreak,
					Paragraph,
					PasteFromMarkdownExperimental,
					PasteFromOffice,
					PlainTableOutput,
					RemoveFormat,
					ShowBlocks,
					SimpleUploadAdapter,
					SourceEditing,
					SpecialCharacters,
					SpecialCharactersArrows,
					SpecialCharactersCurrency,
					SpecialCharactersEssentials,
					SpecialCharactersLatin,
					SpecialCharactersMathematical,
					SpecialCharactersText,
					Strikethrough,
					Style,
					Subscript,
					Superscript,
					Table,
					TableCaption,
					TableCellProperties,
					TableColumnResize,
					TableLayout,
					TableProperties,
					TableToolbar,
					TextPartLanguage,
					TextTransformation,
					Title,
					TodoList,
					Underline,
					WordCount,
					DelayedUploadAdapterPlugin,
				],
				removePlugins: [Title],
				balloonToolbar: ["bold", "italic", "|", "link", "insertImage", "|", "bulletedList", "numberedList"],
				fontFamily: {
					supportAllValues: true,
				},
				fontSize: {
					options: [10, 12, 14, "default", 18, 20, 22],
					supportAllValues: true,
				},
				fullscreen: {
					onEnterCallback: (container) =>
						container.classList.add(
							"editor-container",
							"editor-container_classic-editor",
							"editor-container_include-style",
							"editor-container_include-word-count",
							"editor-container_include-fullscreen",
							"main-container"
						),
				},
				heading: {
					options: [
						{
							model: "paragraph",
							title: "Paragraph",
							class: "ck-heading_paragraph",
						},
						{
							model: "heading1",
							view: "h1",
							title: "Heading 1",
							class: "ck-heading_heading1",
						},
						{
							model: "heading2",
							view: "h2",
							title: "Heading 2",
							class: "ck-heading_heading2",
						},
						{
							model: "heading3",
							view: "h3",
							title: "Heading 3",
							class: "ck-heading_heading3",
						},
						{
							model: "heading4",
							view: "h4",
							title: "Heading 4",
							class: "ck-heading_heading4",
						},
						{
							model: "heading5",
							view: "h5",
							title: "Heading 5",
							class: "ck-heading_heading5",
						},
						{
							model: "heading6",
							view: "h6",
							title: "Heading 6",
							class: "ck-heading_heading6",
						},
					],
				},
				htmlSupport: {
					allow: [
						{
							name: /^.*$/,
							styles: true,
							attributes: true,
							classes: true,
						},
					],
				},
				image: {
					toolbar: [
						"toggleImageCaption",
						"imageTextAlternative",
						"|",
						"imageStyle:inline",
						"imageStyle:wrapText",
						"imageStyle:breakText",
						"|",
						"resizeImage",
					],
				},
				language: "ko",
				licenseKey: LICENSE_KEY,
				link: {
					addTargetToExternalLinks: true,
					defaultProtocol: "https://",
					decorators: {
						toggleDownloadable: {
							mode: "manual",
							label: "Downloadable",
							attributes: {
								download: "file",
							},
						},
					},
				},
				list: {
					properties: {
						styles: true,
						startIndex: true,
						reversed: true,
					},
				},
				mention: {
					feeds: [
						{
							marker: "@",
							feed: [
								/* See: https://ckeditor.com/docs/ckeditor5/latest/features/mentions.html */
							],
						},
					],
				},
				style: {
					definitions: [
						{
							name: "Article category",
							element: "h3",
							classes: ["category"],
						},
						{
							name: "Title",
							element: "h2",
							classes: ["document-title"],
						},
						{
							name: "Subtitle",
							element: "h3",
							classes: ["document-subtitle"],
						},
						{
							name: "Info box",
							element: "p",
							classes: ["info-box"],
						},
						{
							name: "CTA Link Primary",
							element: "a",
							classes: ["button", "button--green"],
						},
						{
							name: "CTA Link Secondary",
							element: "a",
							classes: ["button", "button--black"],
						},
						{
							name: "Marker",
							element: "span",
							classes: ["marker"],
						},
						{
							name: "Spoiler",
							element: "span",
							classes: ["spoiler"],
						},
					],
				},
				table: {
					contentToolbar: ["tableColumn", "tableRow", "mergeTableCells", "tableProperties", "tableCellProperties"],
				},
				translations: [translations],
			};

			const editorConfig = {
				...defaultConfig,
				...config,
			};

			// 에디터 생성
			editor = await ClassicEditor.create(element, editorConfig);

			// 초기 콘텐츠 설정
			if (value) {
				editor.setData(value);
			}

			// 변경 이벤트 처리
			editor.model.document.on("change:data", () => {
				const data = editor.getData();
				dispatch("change", { editor, data });
				value = data;
			});

			// 포커스 이벤트
			editor.editing.view.document.on("focus", () => {
				dispatch("focus", { editor });
			});

			// 블러 이벤트
			editor.editing.view.document.on("blur", () => {
				dispatch("blur", { editor });
			});

			// 준비 완료 이벤트
			dispatch("ready", { editor });
			ready = true;

			// 초기 비활성화 상태 적용
			currentDisabledState = disabled;
			if (disabled && typeof editor.enableReadOnlyMode === "function") {
				editor.enableReadOnlyMode(readOnlyLockId);
			}
		} catch (error) {
			console.error("CKEditor 초기화 오류:", error);
			dispatch("error", { error });
		}
	});

	// 외부에서 value가 변경될 때 에디터 내용 업데이트
	$: if (editor && ready && value !== undefined && value !== null && value !== editor.getData()) {
		editor.setData(value);
	}

	// 비활성화 상태 변경 처리 - 수정된 부분
	$: {
		if (editor && ready && disabled !== currentDisabledState) {
			currentDisabledState = disabled;
			updateReadOnlyState();
		}
	}

	// 읽기 전용 상태 업데이트 함수
	function updateReadOnlyState() {
		if (!editor) return;

		try {
			if (typeof editor.enableReadOnlyMode === "function" && typeof editor.disableReadOnlyMode === "function") {
				// 공식 API 사용
				if (currentDisabledState) {
					editor.enableReadOnlyMode(readOnlyLockId);
				} else {
					editor.disableReadOnlyMode(readOnlyLockId);
				}
			} else {
				// 대체 방법: contenteditable 속성 직접 설정
				const editorElement = editor.ui.getEditableElement();
				if (editorElement) {
					editorElement.setAttribute("contenteditable", currentDisabledState ? "false" : "true");
					if (currentDisabledState) {
						editorElement.classList.add("ck-disabled");
					} else {
						editorElement.classList.remove("ck-disabled");
					}
				}
			}
		} catch (error) {
			console.error("읽기 전용 모드 설정 오류:", error);
		}
	}

	// 컴포넌트 제거 시 에디터 정리
	onDestroy(() => {
		if (editor) {
			editor.destroy().catch((error) => console.error("에디터 정리 오류:", error));
			editor = null;
		}
	});

	// 외부에서 접근 가능한 메서드
	export function getEditor() {
		return editor;
	}

	export function focus() {
		if (editor) {
			editor.focus();
		}
	}

	export function isReadOnly() {
		return editor ? !!editor.isReadOnly : false;
	}

	export function toggleReadOnly(makeReadOnly) {
		if (!editor) return;

		const newState = makeReadOnly === undefined ? !currentDisabledState : !!makeReadOnly;
		if (newState !== currentDisabledState) {
			currentDisabledState = newState;
			updateReadOnlyState();
		}
	}
</script>

<div class="ckeditor-container">
	<div bind:this={element}></div>
</div>

<style>
	.ckeditor-container {
		width: 100%;
	}

	/* CKEditor의 기본 스타일 오버라이드 (필요한 경우) */
	:global(.ck-editor__editable) {
		min-height: 300px;
		max-height: 400px;
		overflow-y: auto;
	}

	/* 모달 내 CKEditor 편집 영역 높이 제한 */
	:global(.modal-body .ck-editor__editable) {
		min-height: 250px;
		max-height: calc(90vh - 380px);
		overflow-y: auto;
		resize: none;
	}

	/* 비활성화된 에디터 스타일 */
	:global(.ck-editor__editable.ck-disabled) {
		background-color: #f5f5f5;
		color: #999;
		cursor: not-allowed;
	}

	/* 툴바 줄바꿈 스타일 */
	:global(.ck-toolbar) {
		flex-wrap: wrap !important;
		max-width: 100% !important;
	}

	:global(.ck-toolbar__items) {
		flex-wrap: wrap !important;
		max-width: 100% !important;
	}

	/* 모달 내부의 CKEditor 툴바 특별 스타일 */
	:global(.modal .ck-toolbar) {
		flex-wrap: wrap !important;
		justify-content: flex-start !important;
	}

	:global(.modal .ck-toolbar__items) {
		flex-wrap: wrap !important;
		overflow: visible !important;
		width: 100% !important;
	}

	/* 툴바 그룹 줄바꿈 허용 */
	:global(.ck-toolbar__group) {
		flex-wrap: wrap !important;
	}

	/* 툴바 아이템 간격 조정 */
	:global(.ck-toolbar__items > *) {
		margin: 2px !important;
	}
</style>
